/**
 * Autocomplete sources for design-tab tags and mentions (design §16).
 * - inlineSuggestions: `#`/`@` typed in a WYSIWYG body → ranked completions.
 * - filterTagSuggestions: the TagsField's comma-separated token → known tags.
 * Pure ranking over caller-supplied vocabularies (known tags most-used-first,
 * concept names from the design index).
 */
import { normalizeConceptName } from './design-index';

export interface InlineSuggestion {
  /** Row text ("#fire" / "Fire Pit"). */
  label: string;
  /** Exact text to insert, sigil included ("#fire" / "@FirePit"). */
  insert: string;
}

/** Compact `@Mention` form of a concept name: "Fire Pit" → "FirePit". */
export function mentionToken(name: string): string {
  return name.replace(/[^A-Za-z0-9_-]+/g, '');
}

export function inlineSuggestions(args: {
  kind: 'tag' | 'mention';
  query: string;
  /** Known tags, most-used first (tagCounts order). */
  tags: string[];
  /** Concept names from the design index. */
  names: string[];
  limit?: number;
}): InlineSuggestion[] {
  const { kind, query, tags, names, limit = 8 } = args;
  if (kind === 'tag') {
    const q = query.toLowerCase();
    return rank(
      dedupe(tags),
      (t) => score(t.toLowerCase(), q),
      (t) => ({ label: `#${t}`, insert: `#${t}` }),
      limit,
    );
  }
  const q = normalizeConceptName(query);
  return rank(
    dedupe(names),
    (n) => score(normalizeConceptName(n), q),
    (n) => ({ label: n, insert: `@${mentionToken(n)}` }),
    limit,
  );
}

/** TagsField: complete the comma-separated token being typed. Excludes tags the
 *  field already holds; empty token shows the most-used tags. */
export function filterTagSuggestions(
  currentToken: string,
  existing: string[],
  known: string[],
  limit = 8,
): string[] {
  const has = new Set(existing.map((t) => t.toLowerCase()));
  const q = currentToken.trim().toLowerCase();
  return rank(
    dedupe(known).filter((t) => !has.has(t.toLowerCase())),
    (t) => score(t.toLowerCase(), q),
    (t) => t,
    limit,
  );
}

function dedupe(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.toLowerCase();
    if (v && !seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  }
  return out;
}

/** 2 = prefix, 1 = substring, 0 = empty query (keep input order), -1 = no match. */
function score(candidate: string, query: string): number {
  if (!query) return 0;
  if (candidate.startsWith(query)) return 2;
  if (candidate.includes(query)) return 1;
  return -1;
}

function rank<T>(
  values: string[],
  scoreOf: (v: string) => number,
  toItem: (v: string) => T,
  limit: number,
): T[] {
  return values
    .map((v, i) => ({ v, s: scoreOf(v), i }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => toItem(x.v));
}
